<template>
  <div class="relative w-full h-full">
    <!-- Reset Password Modal -->
    <div v-if="showResetModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div class="p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-2">Сброс пароля</h2>
          <p class="text-gray-600 mb-4">
            Установите новый пароль для пользователя <strong>{{ selectedRequest?.login }}</strong>
          </p>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Новый пароль</label>
              <div class="relative">
                <input
                  v-model="resetForm.password"
                  :type="showResetPassword ? 'text' : 'password'"
                  class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                  placeholder="Минимум 6 символов"
                />
                <PasswordVisibilityToggle
                  :visible="showResetPassword"
                  @toggle="showResetPassword = !showResetPassword"
                />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Подтверждение пароля</label>
              <div class="relative">
                <input
                  v-model="resetForm.password2"
                  :type="showResetPassword2 ? 'text' : 'password'"
                  class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                  placeholder="Повторите пароль"
                />
                <PasswordVisibilityToggle
                  :visible="showResetPassword2"
                  show-label="Показать повтор пароля"
                  hide-label="Скрыть повтор пароля"
                  @toggle="showResetPassword2 = !showResetPassword2"
                />
              </div>
            </div>

            <div v-if="resetError" class="bg-red-50 border border-red-200 rounded p-3">
              <p class="text-sm text-red-700">{{ resetError }}</p>
            </div>
          </div>
        </div>
        <div class="border-t p-6 flex justify-end gap-3">
          <button
            @click="closeResetModal"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition font-semibold"
          >
            Отмена
          </button>
          <button
            @click="resetPassword"
            :disabled="!isResetFormValid || resetLoading"
            class="px-4 py-2 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition font-semibold disabled:opacity-50"
          >
            {{ resetLoading ? 'Сохранение...' : 'Сбросить пароль' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reject Confirmation Modal -->
    <div v-if="showRejectModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div class="p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-2">Подтверждение отклонения</h2>
          <p class="text-gray-600 mb-4">
            Вы уверены, что хотите отклонить заявку от <strong>{{ selectedRequest?.login }}</strong>?
          </p>
          <p class="text-red-600 text-sm">
            <strong>Внимание:</strong> Пользователь будет заблокирован.
          </p>
        </div>
        <div class="border-t p-6 flex justify-end gap-3">
          <button
            @click="showRejectModal = false"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition font-semibold"
          >
            Отмена
          </button>
          <button
            @click="rejectRequest"
            :disabled="rejectLoading"
            class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition font-semibold"
          >
            {{ rejectLoading ? 'Обработка...' : 'Отклонить и заблокировать' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Header -->
    <div class="relative pb-32">
      <div class="px-4 pt-3 pb-3 bg-white rounded-lg shadow-xl overflow-hidden md:px-10 mx-auto w-full">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-4xl font-bold">Заявки на восстановление</h1>
            <p class="text-blueGray-400 mt-2">Управление заявками на восстановление пароля</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="mx-auto w-full -mt-24 mb-12">
      <!-- Filters Card -->
      <div class="bg-white rounded-lg shadow-xl p-6 mb-6">
        <h2 class="text-blueGray-800 text-lg font-bold mb-4">Фильтры</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-blueGray-600 text-xs uppercase font-bold mb-2">Статус</label>
            <select
              v-model="filters.status"
              @change="currentPage = 1; fetchRequests()"
              class="w-full px-3 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Все статусы</option>
              <option value="pending">Ожидает</option>
              <option value="resolved">Выполнено</option>
              <option value="rejected">Отклонено</option>
            </select>
          </div>

          <div class="md:col-span-2">
            <label class="block text-blueGray-600 text-xs uppercase font-bold mb-2">Поиск по логину</label>
            <input
              v-model="filters.search"
              @keyup.enter="currentPage = 1; fetchRequests()"
              placeholder="Поиск по логину..."
              class="w-full px-3 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 placeholder-blueGray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
      </div>

      <!-- Table Card -->
      <div class="bg-white rounded-lg shadow-xl overflow-hidden">
        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="text-center">
            <i class="fas fa-spinner fa-spin text-4xl text-blueGray-400 mb-2"></i>
            <p class="text-blueGray-600">Загрузка заявок...</p>
          </div>
        </div>

        <div v-else-if="requests.length === 0" class="text-center py-12">
          <i class="fas fa-inbox text-4xl text-blueGray-300 mb-2"></i>
          <p class="text-blueGray-600">Заявок не найдено</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-blueGray-50 border-b border-blueGray-200">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Логин</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Email</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">ФИО</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Статус</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Дата</th>
                <th class="px-6 py-3 text-left text-xs font-bold text-blueGray-600 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="request in requests" :key="request.id" class="border-b border-blueGray-200 hover:bg-blueGray-50 transition">
                <td class="px-6 py-4 text-sm text-blueGray-900 font-semibold">{{ request.login }}</td>
                <td class="px-6 py-4 text-sm text-blueGray-600">{{ request.email }}</td>
                <td class="px-6 py-4 text-sm text-blueGray-600">
                  {{ request.user_fio || 'Пользователь не найден' }}
                  <span v-if="request.user && !request.user_is_enabled" class="ml-2 text-xs text-red-600">(Заблокирован)</span>
                </td>
                <td class="px-6 py-4 text-sm">
                  <span
                    class="px-3 py-1 rounded-full text-xs font-bold"
                    :class="getStatusClass(request.status)"
                  >
                    {{ request.status_display }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-blueGray-600">{{ formatDate(request.created_at) }}</td>
                <td class="px-6 py-4 text-sm space-x-2">
                  <template v-if="request.status === 'pending'">
                    <button
                      v-if="request.user"
                      @click="openResetModal(request)"
                      class="inline-block px-3 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition text-xs font-bold"
                    >
                      Сбросить пароль
                    </button>
                    <button
                      @click="openRejectModal(request)"
                      class="inline-block px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition text-xs font-bold"
                    >
                      Отклонить
                    </button>
                  </template>
                  <template v-else>
                    <span class="text-xs text-blueGray-500">
                      Обработано: {{ request.processed_by_fio || 'N/A' }}
                    </span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-6 flex justify-center items-center gap-4">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 transition"
        >
          ← Назад
        </button>
        <span class="px-4 py-2 text-gray-700">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 transition"
        >
          Вперед →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_PASSWORD_RECOVERY, HEADERS_TOKEN_JSON, DEFAULT_PER_PAGE } from '@/const/uri';
import PasswordVisibilityToggle from "@/components/Forms/PasswordVisibilityToggle.vue";

export default {
  name: 'PasswordRecoveryIndex',
  components: {
    PasswordVisibilityToggle,
  },
  data() {
    return {
      requests: [],
      loading: false,
      currentPage: 1,
      totalPages: 1,
      perPage: DEFAULT_PER_PAGE,
      filters: {
        status: '',
        search: ''
      },
      // Reset password modal
      showResetModal: false,
      selectedRequest: null,
      resetForm: { password: '', password2: '' },
      resetError: '',
      resetLoading: false,
      showResetPassword: false,
      showResetPassword2: false,
      // Reject modal
      showRejectModal: false,
      rejectLoading: false,
    };
  },
  computed: {
    isResetFormValid() {
      return this.resetForm.password.length >= 6 &&
             this.resetForm.password === this.resetForm.password2;
    },
  },
  mounted() {
    this.fetchRequests();
  },
  methods: {
    async fetchRequests() {
      this.loading = true;
      try {
        const params = { p: this.currentPage, page_size: this.perPage };
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.search) params.search = this.filters.search;

        const response = await axios.get(API_PASSWORD_RECOVERY.list, {
          params,
          headers: HEADERS_TOKEN_JSON()
        });

        if (response.data?.data) {
          this.requests = response.data.data.results || [];
          const count = response.data.data.count || 0;
          this.totalPages = Math.ceil(count / this.perPage) || 1;
        }
      } catch (error) {
        console.error('Error fetching requests:', error);
      } finally {
        this.loading = false;
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchRequests();
      }
    },

    openResetModal(request) {
      this.selectedRequest = request;
      this.resetForm = { password: '', password2: '' };
      this.resetError = '';
      this.showResetPassword = false;
      this.showResetPassword2 = false;
      this.showResetModal = true;
    },

    closeResetModal() {
      this.showResetModal = false;
      this.selectedRequest = null;
      this.showResetPassword = false;
      this.showResetPassword2 = false;
    },

    async resetPassword() {
      if (!this.isResetFormValid) return;

      this.resetLoading = true;
      this.resetError = '';

      try {
        await axios.post(
          `${API_PASSWORD_RECOVERY.base}${this.selectedRequest.id}/reset-password/`,
          {
            password: this.resetForm.password,
            password2: this.resetForm.password2
          },
          { headers: HEADERS_TOKEN_JSON() }
        );

        this.closeResetModal();
        await this.fetchRequests();
      } catch (error) {
        console.error('Error resetting password:', error);
        this.resetError = error.response?.data?.error || 'Ошибка при сбросе пароля';
      } finally {
        this.resetLoading = false;
      }
    },

    openRejectModal(request) {
      this.selectedRequest = request;
      this.showRejectModal = true;
    },

    async rejectRequest() {
      this.rejectLoading = true;

      try {
        await axios.post(
          `${API_PASSWORD_RECOVERY.base}${this.selectedRequest.id}/reject/`,
          {},
          { headers: HEADERS_TOKEN_JSON() }
        );

        this.showRejectModal = false;
        this.selectedRequest = null;
        await this.fetchRequests();
      } catch (error) {
        console.error('Error rejecting request:', error);
      } finally {
        this.rejectLoading = false;
      }
    },

    getStatusClass(status) {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-800',
        resolved: 'bg-emerald-100 text-emerald-800',
        rejected: 'bg-red-100 text-red-800'
      };
      return classes[status] || 'bg-blueGray-100 text-blueGray-800';
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>
